import './instrument';
import * as Sentry from '@sentry/react';
import * as ReactDOM from 'react-dom/client';
import App from './app/app';
import {
  ErrorBoundary,
  AuthProvider,
  CurrentCompanyProvider,
} from '@employ-engage/common-components';
import { ApolloProvider, ApolloClient, InMemoryCache } from '@apollo/client';
import { canonicalRedirectUrl, requireBuildEnv } from '@employ-engage/data';
import SuperTokens from 'supertokens-web-js';
import Session from 'supertokens-web-js/recipe/session';
import EmailPassword from 'supertokens-web-js/recipe/emailpassword';
import 'shepherd.js/dist/css/shepherd.css';

// Fail here, naming the variable, rather than handing an empty string to
// SuperTokens and Apollo and getting a blank page out of the library.
const apiEndpoint = requireBuildEnv(
  'VITE_API_ENDPOINT',
  import.meta.env.VITE_API_ENDPOINT
);

// See the portal's copy of this: the raw fly.dev hostname fails CORS.
const canonicalUrl = canonicalRedirectUrl(
  window.location.href,
  import.meta.env.VITE_WEBSITE_DOMAIN
);
if (canonicalUrl) {
  window.location.replace(canonicalUrl);
}

SuperTokens.init({
  appInfo: {
    appName: 'Employ Engage',
    apiDomain: apiEndpoint,
    apiBasePath: '/auth',
  },
  recipeList: [
    Session.init({
      sessionTokenFrontendDomain: import.meta.env.VITE_WEBSITE_DOMAIN
        ? '.' + new URL(import.meta.env.VITE_WEBSITE_DOMAIN).hostname
        : undefined,
    }),
    EmailPassword.init(),
  ],
});

const client = new ApolloClient({
  uri: `${apiEndpoint}/graphql`,
  cache: new InMemoryCache(),
  credentials: 'include',
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
  {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  }
);
root.render(
  <ErrorBoundary>
    <ApolloProvider client={client}>
      <CurrentCompanyProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </CurrentCompanyProvider>
    </ApolloProvider>
  </ErrorBoundary>
);
